/*
 * CancelBookingPage.jsx
 * -----------------------------------------------------------------------------
 * Guest-facing workflow that lets a visitor review an existing booking by ID
 * and trigger the public cancellation endpoint after confirming their intent.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import useApi from '../hooks/useApi';
import createRageRoomController from '../hooks/controllers/rageRoomController';
import Footer from '../components/Footer';
const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;
const FALLBACK_BACKGROUND_GRADIENT =
  'radial-gradient(circle at 20% 20%, rgba(255,242,0,0.18) 0%, transparent 45%), radial-gradient(circle at 80% 0%, rgba(250,239,237,0.10) 0%, transparent 40%)';

/**
 * Normalizes a booking response object, regardless of whether the API wraps it.
 *
 * @param {Object} response - API response from the server.
 * @returns {Object|null} Booking document or null if missing.
 */
const extractBookingFromResponse = (response) => {
  if (!response) {
    return null;
  }
  if (response.booking) {
    return response.booking;
  }
  return response;
};

/**
 * Formats a date string into a user-friendly representation.
 *
 * @param {string|Date} value - Raw date value.
 * @returns {string} Readable date or fallback label.
 */
const formatDisplayDate = (value) => {
  if (!value) {
    return 'Date to be determined';
  }
  try {
    return new Date(value).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (err) {
    return String(value);
  }
};

/**
 * Converts slot/time information into a readable string.
 *
 * @param {string} slot - Raw slot label.
 * @returns {string} Formatted slot string.
 */
const formatSlot = (slot) => {
  if (!slot) {
    return '—';
  }
  return slot;
};

/**
 * Renders a single booking detail row.
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - Descriptor for the value.
 * @param {string} props.value - Value to display.
 * @returns {JSX.Element} Rendered label/value pair.
 */
const BookingDetailRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    <Typography variant="body1" fontWeight={600}>
      {label}:
    </Typography>
    <Typography variant="body1" color="text.secondary">
      {value || '—'}
    </Typography>
  </Stack>
);

/**
 * Page component that surfaces the public cancellation workflow.
 *
 * @returns {JSX.Element} Cancel booking form.
 */
const CancelBookingPage = () => {
  const { bookingId } = useParams();
  const { rageRoom } = useApi();

  const publicBookingApi = rageRoom?.public;
  const getBookingRequest = publicBookingApi?.getBooking;
  const cancelBookingRequest = publicBookingApi?.cancelBooking;

  const [booking, setBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [bookingError, setBookingError] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelState, setCancelState] = useState({
    loading: false,
    success: null,
    error: null
  });

  const isAlreadyCancelled = booking?.status === 'cancelled';
  const slotSummary = Array.isArray(booking?.slotLabels) && booking.slotLabels.length
    ? booking.slotLabels.join(', ')
    : booking?.slotLabel || '—';
  const guestCount = booking?.slotQuantity
    || (Array.isArray(booking?.slotLabels) ? booking.slotLabels.length : 0)
    || 1;
  const statusLabel = booking?.status
    ? `${booking.status.charAt(0).toUpperCase()}${booking.status.slice(1)}`
    : '—';

  useEffect(() => {
    if (!bookingId) {
      setBookingLoading(false);
      return;
    }
    if (!getBookingRequest) {
      return;
    }

    let isSubscribed = true;

    /**
     * Loads booking details by identifier so we can confirm context for the user.
     */
    const fetchBooking = async () => {
      setBookingLoading(true);
      setBookingError(null);
      try {
        const response = await getBookingRequest({ bookingId });
        if (!isSubscribed) {
          return;
        }
        setBooking(extractBookingFromResponse(response));
      } catch (err) {
        if (!isSubscribed) {
          return;
        }
        setBookingError(err?.message || 'Unable to load booking details.');
      } finally {
        if (isSubscribed) {
          setBookingLoading(false);
        }
      }
    };

    fetchBooking();

    return () => {
      isSubscribed = false;
    };
  }, [bookingId]);

  /**
   * Calls the cancellation endpoint with an optional reason provided by the guest.
   */
  const handleCancelBooking = async () => {
    if (!bookingId || !cancelBookingRequest || cancelState.loading || isAlreadyCancelled || bookingLoading) {
      return;
    }
    setCancelState({
      loading: true,
      success: null,
      error: null
    });
    try {
      const payload = cancelReason.trim()
        ? { cancellationReason: cancelReason.trim() }
        : {};
      const response = await cancelBookingRequest({
        bookingId,
        payload
      });
      setBooking(extractBookingFromResponse(response));
      setCancelState({
        loading: false,
        success: 'Your booking has been cancelled.',
        error: null
      });
    } catch (err) {
      setCancelState({
        loading: false,
        success: null,
        error: err?.message || 'Unable to cancel the booking right now.'
      });
    }
  };

  const isCancelDisabled = !bookingId
    || !cancelBookingRequest
    || cancelState.loading
    || isAlreadyCancelled
    || bookingLoading;

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 1, md: 2 },
        px: 1,
        backgroundColor: 'background.default',
        backgroundImage: `var(--pluto-background-image, ${FALLBACK_BACKGROUND_GRADIENT})`
      }}
    >
      <Stack spacing={{ xs: 1, md: 2 }} alignItems="center" width="100%">
        <Box
          component="img"
          src={PLUTO_LOGO_PATH}
          alt="Pluto TV logo"
          sx={{
            height: { xs: 56, sm: 160 },
            width: 'auto',
            filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.6))'
          }}
        />
        <Container maxWidth="sm" sx={{ width: '100%' }}>
          <Card>
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <Stack spacing={3}>
                <Box textAlign="center">
                  <Typography variant="h4" gutterBottom>
                    Cancel Booking
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Use this page to review your rage room reservation and confirm a cancellation.
                  </Typography>
                </Box>

                {!bookingId && (
                  <Alert severity="error">
                    Missing booking identifier. Please confirm the link provided in your confirmation email.
                  </Alert>
                )}

                {/*
                {bookingId && (
                  <Alert severity="info">
                    Booking ID: <strong>{bookingId}</strong>
                  </Alert>
                )}
                */}
                {bookingId && bookingLoading && (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                )}

                {bookingId && !bookingLoading && booking && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Booking details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1.5}>
                      <BookingDetailRow label="Status" value={statusLabel} />
                      <BookingDetailRow label="Date" value={formatDisplayDate(booking?.date)} />
                      <BookingDetailRow label="Time Slot" value={formatSlot(booking?.timeSlot)} />
                      <BookingDetailRow label="Session" value={booking?.sessionLabel || '—'} />
                      <BookingDetailRow label="Room" value={booking?.roomName || '—'} />
                      <BookingDetailRow label="Location" value={booking?.locationName || booking?.city || '—'} />
                      <BookingDetailRow label="Guest Count" value={String(guestCount)} />
                      <BookingDetailRow label="Slots" value={slotSummary} />
                      <BookingDetailRow label="Primary Contact" value={booking?.customer?.name || 'Guest'} />
                      <BookingDetailRow
                        label="Contact Info"
                        value={
                          booking?.customer
                            ? [booking.customer.email, booking.customer.phoneNumber].filter(Boolean).join(' • ')
                            : '—'
                        }
                      />
                    </Stack>
                  </Box>
                )}

                {bookingId && !bookingLoading && bookingError && (
                  <Alert severity="warning">
                    {bookingError} You can still submit a cancellation request using the form below.
                  </Alert>
                )}

                {cancelState.success && (
                  <Alert
                    severity="success"
                    onClose={() => setCancelState((prev) => ({ ...prev, success: null }))}
                  >
                    {cancelState.success}
                  </Alert>
                )}

                {cancelState.error && (
                  <Alert
                    severity="error"
                    onClose={() => setCancelState((prev) => ({ ...prev, error: null }))}
                  >
                    {cancelState.error}
                  </Alert>
                )}

                <Stack spacing={2}>
                  <TextField
                    label="Cancellation reason (optional)"
                    value={cancelReason}
                    onChange={(event) => setCancelReason(event.target.value)}
                    multiline
                    minRows={2}
                    placeholder="Let us know why you need to cancel..."
                    disabled={isCancelDisabled}
                  />
                  <Button
                    variant="contained"
                    color={isAlreadyCancelled ? 'secondary' : 'error'}
                    size="large"
                    disabled={isCancelDisabled}
                    onClick={handleCancelBooking}
                  >
                    {cancelState.loading ? 'Cancelling...' : isAlreadyCancelled ? 'Booking Cancelled' : 'Cancel Booking'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Cancelling frees up your slot for other guests. If you need to reschedule, please return to the booking
                    page after cancelling.
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
          <Footer />
        </Container>
      </Stack>
    
    </Box>
  );
};

export default CancelBookingPage;